import { Box, Button, Flex, Icon, Text } from "@chakra-ui/react";
import { Accordion } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { Link as RouterLink, useRouterState } from "@tanstack/react-router";
import { FiMessageSquare, FiUsers, FiTool } from "react-icons/fi";
import { TiMessages } from "react-icons/ti";
import { TbReport } from "react-icons/tb";
import { LuFileOutput, LuFileText, LuSettings2, LuTable } from "react-icons/lu";
import type { IconType } from "react-icons/lib";
import { PiNotePencilBold } from "react-icons/pi";

import type { UserPublic } from "@/client";

interface MenuItem {
  icon: IconType;
  title: string;
  path?: string;
  children?: MenuItem[];
}

interface MenuCategory {
  title: string;
  items: MenuItem[];
}

const menuCategories: MenuCategory[] = [
  {
    title: "서비스",
    items: [
      { icon: PiNotePencilBold, title: "AI 메모", path: "/writings" },
      { icon: TbReport, title: "AI 리포트", path: "/reports" },
    ],
  },
  {
    title: "지식 자산 관리",
    items: [
      { icon: LuFileText, title: "문서관리", path: "/documentList" },
      { icon: LuTable, title: "테이블관리", path: "/tableList" },
      { icon: LuFileOutput, title: "데이터 추출", path: "/extraction" },
    ],
  },
  {
    title: "시스템",
    items: [
      { icon: FiTool, title: "도구 관리", path: "/tools" },
      { icon: LuSettings2, title: "설정", path: "/settings" },
    ],
  },
];

interface SidebarItemsProps {
  onClose?: () => void;
  isCollapsed?: boolean;
}

const SidebarItems = ({ onClose, isCollapsed = false }: SidebarItemsProps) => {
  const queryClient = useQueryClient();
  const currentUser = queryClient.getQueryData<UserPublic>(["currentUser"]);

  // useRouterState를 사용하여 실시간 라우터 상태 감지
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  // Add admin category if user is superuser
  const finalCategories: MenuCategory[] = currentUser?.is_superuser
    ? [
        ...menuCategories,
        {
          title: "관리자",
          items: [{ icon: FiUsers, title: "사용자 관리", path: "/admin" }],
        },
      ]
    : menuCategories;

  const renderMenuItem = (item: MenuItem) => {
    const isActive = currentPath === item.path;

    if (item.children) {
      return (
        <Box key={item.title}>
          <Text
            px={4}
            py={2}
            fontSize="sm"
            fontWeight="semibold"
            color="gray.600"
          >
            {item.title}
          </Text>
          {item.children.map((child) => renderMenuItem(child))}
        </Box>
      );
    }

    return (
      <RouterLink key={item.title} to={item.path || "#"} onClick={onClose}>
        <Flex
          gap={isCollapsed ? 0 : 3}
          px={isCollapsed ? 3 : 4}
          py={3}
          mx={2}
          borderRadius="lg"
          _hover={{
            background: isActive ? "blue.100" : "gray.100",
            transform: "translateX(2px)",
            transition: "all 0.2s",
          }}
          bg={isActive ? "blue.50" : "transparent"}
          borderLeft={isActive ? "3px solid" : "3px solid transparent"}
          borderColor={isActive ? "blue.500" : "transparent"}
          alignItems="center"
          fontSize="sm"
          flex={1}
          cursor="pointer"
          transition="all 0.2s"
        >
          <Icon
            as={item.icon}
            alignSelf="center"
            boxSize={5}
            color={isActive ? "blue.600" : "gray.600"}
          />
          {!isCollapsed && (
            <Text
              ml={2}
              fontWeight={isActive ? "semibold" : "medium"}
              color={isActive ? "blue.700" : "gray.700"}
            >
              {item.title}
            </Text>
          )}
        </Flex>
      </RouterLink>
    );
  };

  return (
    <Box w="100%">
      {/* 대화하기 Button */}
      <Box px={4} py={4} mb={1}>
        <RouterLink to="/conversations" onClick={onClose}>
          <Button
            size="lg"
            width="100%"
            colorScheme="blue"
            borderRadius="xl"
            justifyContent="center"
            px={isCollapsed ? 2 : 4}
            py={6}
            boxShadow="md"
            _hover={{
              transform: "translateY(-1px)",
              boxShadow: "lg",
            }}
            transition="all 0.2s"
          >
            <Flex alignItems="center" justifyContent="center" gap={2}>
              <Icon as={FiMessageSquare} boxSize={5} />
              {!isCollapsed && <Text fontWeight="semibold">대화하기</Text>}
            </Flex>
          </Button>
        </RouterLink>
      </Box>

      {/* 독립 메뉴 섹션 - 아코디언 밖에 위치 */}
      <Box px={2} mb={2}>
        {renderMenuItem({
          icon: TiMessages,
          title: "내 채팅 기록",
          path: "/chatList",
        })}
      </Box>

      {/* Menu Section */}
      {isCollapsed ? (
        // Collapsed view - show all items without accordion
        <Box w="100%" px={2}>
          {finalCategories
            .flatMap((category) => category.items)
            .map(renderMenuItem)}
        </Box>
      ) : (
        // Expanded view - show accordion
        <>
          {/* <Text 
            fontSize="md" 
            px={6} 
            py={3} 
            fontWeight="bold" 
            color="gray.600"
            textTransform="uppercase"
            letterSpacing="wide"
          >
            메뉴
          </Text> */}

          <Accordion.Root
            multiple
            // defaultValue={["대시보드", "서비스"]}
            variant="outline"
          >
            {finalCategories.map((category) => (
              <Accordion.Item key={category.title} value={category.title}>
                <Accordion.ItemTrigger
                  px={6}
                  py={3}
                  fontSize="sm"
                  fontWeight="semibold"
                  color="gray.700"
                  _hover={{ bg: "gray.100" }}
                  borderRadius="md"
                  mx={2}
                  transition="all 0.2s"
                >
                  <Flex justify="space-between" align="center" w="100%">
                    <Text>{category.title}</Text>
                    <Accordion.ItemIndicator />
                  </Flex>
                </Accordion.ItemTrigger>
                <Accordion.ItemContent>
                  <Accordion.ItemBody p={0} pt={1}>
                    {category.items.map(renderMenuItem)}
                  </Accordion.ItemBody>
                </Accordion.ItemContent>
              </Accordion.Item>
            ))}
          </Accordion.Root>
        </>
      )}
    </Box>
  );
};

export default SidebarItems;
